import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRestApi } from 'hooks/useRestApi';
import { useSnackbar } from 'hooks/useSnackbar';
import { apiUrl } from 'utils/apiUrl';
import { debounce } from 'lodash';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { useTranslation } from 'react-i18next';
import { CohortNotifications } from 'App.types';
import { handleApiError } from '../utils';

export interface SiteResponse {
  captain_id: string;
  learn_id?: string;
  learn_url?: string;
  client_name?: string;
  fleet_name?: string;
  aws_account?: string;
  account_name?: string;
  tenant_id?: string;
  cohort?: string;
  cohort_name?: string;
  is_candidate?: string;
  cohort_downtime_date?: string;
  db_size?: string;
  notifications: CohortNotifications;
  [key: string]: any;
}

interface SitesApiResponse {
  items: SiteResponse[];
  next_token: string | null;
}

interface SiteGetFailure {
  id: string;
  reason: string;
}

interface BulkSitesResponse {
  items: SiteResponse[];
  failures: SiteGetFailure[];
}

interface SiteFilters {
  captainId: string;
  learnUrl: string;
  clientName: string;
}

interface UseSiteFilteringOptions {
  isDialogOpen?: boolean;
  debounceDelay?: number;
}

const RESULTS_PER_PAGE = 50;

export const useSiteFiltering = ({
  isDialogOpen = false,
  debounceDelay = 300,
}: UseSiteFilteringOptions = {}) => {
  const { selectedRegion } = useCohortsContext();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // STATE - Filter Management
  const [filterInputs, setFilterInputs] = useState<SiteFilters>({
    captainId: '',
    learnUrl: '',
    clientName: '',
  });

  const [appliedFilters, setAppliedFilters] = useState<SiteFilters>({
    captainId: '',
    learnUrl: '',
    clientName: '',
  });

  // STATE - Data Management
  const [sites, setSites] = useState<SiteResponse[]>([]);
  const [allLoadedSites, setAllLoadedSites] = useState<SiteResponse[]>([]);

  // STATE - Pagination Management
  const [currentPage, setCurrentPage] = useState(0);
  const [pageTokens, setPageTokens] = useState<Map<number, string | null>>(new Map([[0, null]]));
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [filterVersion, setFilterVersion] = useState(0);

  // REFS - Component Lifecycle & Data Tracking
  const isMountedRef = useRef(true);
  const isDialogOpenRef = useRef(isDialogOpen);
  const loadSitesRef = useRef<(pageIndex: number) => Promise<void>>();
  const pageTokensRef = useRef<Map<number, string | null>>(new Map([[0, null]]));
  const currentPageRef = useRef(0);

  // EFFECTS - Ref Synchronization

  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
    isMountedRef.current = isDialogOpen;
  }, [isDialogOpen]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // API HOOKS - Data Fetching
  const {
    data: siteData,
    loading: loadingSites,
    fetch: fetchSites,
  } = useRestApi<SitesApiResponse>(apiUrl('lct', `regions/${selectedRegion}/search`), {
    manual: true,
  });

  const {
    data: singleSiteData,
    loading: loadingSingleSite,
    fetch: fetchSingleSite,
  } = useRestApi<SiteResponse>('', { manual: true });

  const { loading: loadingBulkSites, fetch: fetchBulkSites } = useRestApi<BulkSitesResponse>('', {
    manual: true,
  });

  // COMPUTED VALUES - Filter Logic
  const shouldUseSingleSiteEndpoint = useMemo(
    () =>
      appliedFilters.captainId.trim() !== '' &&
      appliedFilters.learnUrl.trim() === '' &&
      appliedFilters.clientName.trim() === '',
    [appliedFilters.captainId, appliedFilters.learnUrl, appliedFilters.clientName],
  );

  const shouldDisableApply = useMemo(
    () =>
      filterInputs.captainId.trim() !== '' &&
      (filterInputs.learnUrl.trim() !== '' || filterInputs.clientName.trim() !== ''),
    [filterInputs.captainId, filterInputs.learnUrl, filterInputs.clientName],
  );

  const hasActiveFilters = useMemo(
    () =>
      appliedFilters.captainId.trim() !== '' ||
      appliedFilters.learnUrl.trim() !== '' ||
      appliedFilters.clientName.trim() !== '',
    [appliedFilters],
  );

  const visitedPages = useMemo(
    () => Array.from(pageTokensRef.current.keys()).sort((a, b) => a - b),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageTokens, filterVersion],
  );

  const isLoading = loadingSites || loadingSingleSite;

  // CALLBACKS - Filter Actions
  const resetFilters = useCallback(() => {
    setFilterInputs({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });
    setAppliedFilters({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });
    setSites([]);
    setCurrentPage(0);
    const initialTokens = new Map([[0, null]]);
    setPageTokens(initialTokens);
    pageTokensRef.current = initialTokens;
    setNextToken(null);
    setHasMorePages(false);
    setAllLoadedSites([]);
  }, []);

  const handleFilterInputChange = useCallback((field: keyof SiteFilters, value: string) => {
    setFilterInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const applyFilters = useCallback(() => {
    const newFilters = {
      captainId: filterInputs.captainId,
      learnUrl: filterInputs.learnUrl,
      clientName: filterInputs.clientName,
    };

    setFilterVersion((prev) => prev + 1);
    setCurrentPage(0);
    currentPageRef.current = 0;
    const initialTokens = new Map([[0, null]]);
    setPageTokens(initialTokens);
    pageTokensRef.current = initialTokens;
    setNextToken(null);
    setHasMorePages(false);
    setAllLoadedSites([]);
    setSites([]);
    setAppliedFilters(newFilters);
  }, [filterInputs]);

  const clearFilters = useCallback(() => {
    setCurrentPage(0);
    currentPageRef.current = 0;
    const initialTokens = new Map([[0, null]]);
    setPageTokens(initialTokens);
    pageTokensRef.current = initialTokens;
    setNextToken(null);
    setHasMorePages(false);
    setFilterInputs({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });
    setAppliedFilters({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });
    setSites([]);
  }, []);

  const getFilterValidationMessage = useCallback(() => {
    if (shouldDisableApply) {
      return t('cohorts.filterValidation.captainIdExclusive');
    }
    return '';
  }, [shouldDisableApply, t]);

  // CALLBACKS - Data Loading
  const loadSites = useCallback(
    async (pageIndex = 0) => {
      const tokenForPage = pageTokensRef.current.get(pageIndex) ?? null;

      try {
        if (shouldUseSingleSiteEndpoint) {
          const url = apiUrl('lct', `regions/${selectedRegion}/site/${appliedFilters.captainId}`);
          await fetchSingleSite({ url });
        } else {
          const params = {
            is_candidate: 'yes',
            cohort: 'NO_COHORT_ASSIGNED',
            results_count: RESULTS_PER_PAGE,
            ...(tokenForPage && { next_token: tokenForPage }),
            ...(appliedFilters.learnUrl && { learn_url: appliedFilters.learnUrl }),
            ...(appliedFilters.clientName && { client_name: appliedFilters.clientName }),
          };
          await fetchSites({ params });
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message === 'Cancel') {
          return;
        }
        handleApiError(error, { operation: 'loadSites', enqueueSnackbar, t });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      shouldUseSingleSiteEndpoint,
      appliedFilters.captainId,
      appliedFilters.learnUrl,
      appliedFilters.clientName,
      fetchSites,
      fetchSingleSite,
      selectedRegion,
    ],
  );

  const bulkSearchByCaptainIds = useCallback(
    async (captainIds: string[]) => {
      try {
        const baseUrl = apiUrl('lct', `regions/${selectedRegion}/sites`);

        const searchParams = new URLSearchParams();
        captainIds.forEach((id) => searchParams.append('captain_ids', id));

        const url = `${baseUrl}?${searchParams.toString()}`;

        const requestConfig = {
          url,
          method: 'GET' as const,
        };

        const response = await fetchBulkSites(requestConfig);

        return response;
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message === 'Cancel') {
          return null;
        }
        handleApiError(error, { operation: 'searchSites', enqueueSnackbar, t });
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion, fetchBulkSites],
  );

  const processBulkSearchResults = useCallback((rawSites: SiteResponse[]) => {
    const validCandidates: SiteResponse[] = [];
    const sitesInCohort: Array<{
      captain_id: string;
      cohort: string;
      cohort_name: string;
      learn_url?: string;
    }> = [];
    const sitesNotCandidate: Array<{ captain_id: string; reason: string; learn_url?: string }> = [];

    rawSites.forEach((site) => {
      const hasNoCohort = !site.cohort || site.cohort === 'NO_COHORT_ASSIGNED';
      const isValidCandidate = site.is_candidate === 'yes' || site.is_candidate === 'true';

      if (hasNoCohort && isValidCandidate) {
        validCandidates.push(site);
      } else if (!hasNoCohort) {
        sitesInCohort.push({
          captain_id: site.captain_id,
          cohort: site.cohort || 'Unknown',
          cohort_name: site.cohort_name || site.cohort || 'Unknown',
          learn_url: site.learn_url,
        });
      } else if (!isValidCandidate) {
        sitesNotCandidate.push({
          captain_id: site.captain_id,
          reason: site.is_candidate || 'Unknown reason',
          learn_url: site.learn_url,
        });
      }
    });

    return { validCandidates, sitesInCohort, sitesNotCandidate };
  }, []);

  const applyBulkSearchResults = useCallback((validCandidates: SiteResponse[]) => {
    setFilterVersion((prev) => prev + 1);
    setCurrentPage(0);
    currentPageRef.current = 0;
    const initialTokens = new Map([[0, null]]);
    setPageTokens(initialTokens);
    pageTokensRef.current = initialTokens;
    setNextToken(null);
    setHasMorePages(false);

    setFilterInputs({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });
    setAppliedFilters({
      captainId: '',
      learnUrl: '',
      clientName: '',
    });

    setSites(validCandidates);
    setAllLoadedSites(validCandidates);
  }, []);

  // CALLBACKS - Pagination Actions
  const goToPage = useCallback((pageIndex: number) => {
    if (pageIndex >= 0 && pageTokensRef.current.has(pageIndex)) {
      setCurrentPage(pageIndex);
    }
  }, []);

  const goToNextPage = useCallback(() => {
    if (hasMorePages && nextToken) {
      const nextPageIndex = currentPage + 1;
      goToPage(nextPageIndex);
    }
  }, [hasMorePages, nextToken, currentPage, goToPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const getPaginationInfo = useCallback(() => {
    const start = currentPage * RESULTS_PER_PAGE + 1;
    const end = start + sites.length - 1;
    const hasMore = hasMorePages ? '+' : '';
    return {
      start,
      end,
      currentPageSize: sites.length,
      hasMore,
    };
  }, [currentPage, sites.length, hasMorePages]);

  // EFFECTS - Debounced Load Setup
  useEffect(() => {
    loadSitesRef.current = loadSites;
  }, [loadSites, appliedFilters]);

  const debouncedLoadSites = useMemo(
    () => debounce(loadSites, debounceDelay),
    [loadSites, debounceDelay],
  );

  // EFFECTS - Data Loading Triggers
  useEffect(() => {
    if (isDialogOpen && hasActiveFilters) {
      if (loadSitesRef.current) {
        loadSitesRef.current(currentPage);
      }
    }
  }, [isDialogOpen, filterVersion, currentPage, hasActiveFilters]);

  useEffect(() => {
    if (!isDialogOpen) {
      resetFilters();
      debouncedLoadSites.cancel();
    }
  }, [isDialogOpen, resetFilters, debouncedLoadSites]);

  // EFFECTS - Cleanup
  useEffect(
    () => () => {
      isMountedRef.current = false;
      isDialogOpenRef.current = false;
      debouncedLoadSites.cancel();
    },
    [debouncedLoadSites],
  );

  // EFFECTS - Single Site Data Processing
  useEffect(() => {
    if (shouldUseSingleSiteEndpoint && singleSiteData) {
      const hasNoCohort = !singleSiteData.cohort || singleSiteData.cohort === 'NO_COHORT_ASSIGNED';
      const isValidCandidate =
        singleSiteData.is_candidate === 'true' || singleSiteData.is_candidate === 'yes';
      const validSites = hasNoCohort && isValidCandidate ? [singleSiteData] : [];

      setSites(validSites);
      setAllLoadedSites((prev) => {
        const existingIds = new Set(prev.map((s) => s.captain_id));
        const newSites = validSites.filter((s) => !existingIds.has(s.captain_id));
        return [...prev, ...newSites];
      });
      setHasMorePages(false);
      setNextToken(null);
    }
  }, [singleSiteData, shouldUseSingleSiteEndpoint]);

  // EFFECTS - Multi-Site Data Processing
  useEffect(() => {
    if (!shouldUseSingleSiteEndpoint && siteData?.items) {
      const pageForThisData = currentPageRef.current;

      setSites(siteData.items);
      setAllLoadedSites((prev) => {
        const existingIds = new Set(prev.map((s) => s.captain_id));
        const newSites = siteData.items.filter((s) => !existingIds.has(s.captain_id));
        return [...prev, ...newSites];
      });

      const hasNext = !!siteData.next_token;
      setHasMorePages(hasNext);
      setNextToken(siteData.next_token);

      if (hasNext && siteData.next_token) {
        const nextPageIndex = pageForThisData + 1;
        setPageTokens((prev) => {
          if (!prev.has(nextPageIndex)) {
            const updated = new Map(prev);
            updated.set(nextPageIndex, siteData.next_token);
            pageTokensRef.current = updated;
            return updated;
          }
          return prev;
        });
      }
    }
  }, [siteData, shouldUseSingleSiteEndpoint]);

  return {
    // Filter state
    filterInputs,
    appliedFilters,
    hasActiveFilters,
    shouldDisableApply,
    shouldUseSingleSiteEndpoint,

    // Data state
    sites,
    allLoadedSites,
    isLoading,

    // Filter actions
    handleFilterInputChange,
    applyFilters,
    clearFilters,
    getFilterValidationMessage,

    // Pagination state
    currentPage,
    visitedPages,
    hasMorePages,
    hasPreviousPage: currentPage > 0,

    // Pagination actions
    goToPage,
    goToNextPage,
    goToPreviousPage,
    getPaginationInfo,

    bulkSearchByCaptainIds,
    processBulkSearchResults,
    applyBulkSearchResults,
    isLoadingBulkSearch: loadingBulkSites,
  };
};
